import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Button } from '@material-ui/core';
import { RootState } from '../../../store/reduxTypes';
import { ArrowRightIcon } from '../../Icons';
import { CRM_PAGE } from '../../../constants/pathConsts';
import useNavigate from '../../../hooks/useNavigate';
import { PageCallout } from '../../PageCallout';

interface StandardPageProps {
  header?: string;
  title?: string;
  topIcon?: React.ReactNode;
  desc?: string;
  learnMoreLinkUrl?: string;
  startIconForActionButton?: React.ReactNode;
  endIconForActionButton?: React.ReactNode;
  labelForActionButton?: React.ReactNode;
  onAction?: () => void;
  actionButton?: React.ReactNode;
  noClientTitle?: string;
  noClientDesc?: string;
  hideGoToCRMButton?: boolean;
  titleForClient?: string;
  descForClient?: string;
  actionButtonForClient?: React.ReactNode;
  showContent?: boolean;
  navAction?: {
    path: string;
    label: string;
  };
  defaultMode?: boolean; // if true don't apply any settings based on client or internal user, and only uses the base properties i.e. title, topIcon, desc and actionButton.... rest are ignored
  disableHideChannelListToolbar?: boolean;
}

export const StandardPage: React.FC<StandardPageProps> = ({
  title,
  topIcon,
  desc,
  learnMoreLinkUrl,
  startIconForActionButton,
  endIconForActionButton,
  labelForActionButton,
  onAction,
  actionButton,
  noClientTitle = '',
  noClientDesc = '',
  hideGoToCRMButton = false,
  titleForClient = '',
  descForClient = '',
  actionButtonForClient,
  showContent = false,
  navAction,
  children,
  defaultMode = false,
}) => {
  const isClient = useSelector((state: RootState) => state.user.isClient);

  const { navigate } = useNavigate();
  const assignedClients = useSelector(
    (state: RootState) => state.clients.assignedClients,
  );
  if (isClient && !titleForClient && !defaultMode) return null;

  let titleToUse = title;
  let descToUse = desc;
  let actionBtnToUse = actionButton;

  if (isClient && !defaultMode) {
    titleToUse = titleForClient;
    descToUse = descForClient;
    actionBtnToUse = actionButtonForClient;
  } else if ((!assignedClients || assignedClients.length < 1) && !defaultMode) {
    if (noClientTitle) {
      titleToUse = noClientTitle;
      descToUse = noClientDesc;
      actionBtnToUse = hideGoToCRMButton ? null : (
        <Button
          color="primary"
          variant="contained"
          endIcon={<ArrowRightIcon />}
          onClick={() => navigate(CRM_PAGE.path)}
        >
          Go to Clients
        </Button>
      );
    }
  }

  const renderActionButton = () => {
    if (actionBtnToUse) {
      return actionBtnToUse;
    }

    if (navAction) {
      return (
        <Button
          color="primary"
          variant="contained"
          endIcon={<ArrowRightIcon />}
          onClick={() => navigate(navAction.path)}
        >
          {`Go to ${navAction.label}`}
        </Button>
      );
    }

    if (onAction) {
      return (
        <Button
          variant="contained"
          color="primary"
          {...(startIconForActionButton
            ? { startIcon: startIconForActionButton }
            : {})}
          {...(endIconForActionButton
            ? { startIcon: endIconForActionButton }
            : {})}
          onClick={onAction}
        >
          {labelForActionButton}
        </Button>
      );
    }

    return null;
  };

  return showContent ? (
    // needed to satisfy React.FC return type
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{children}</>
  ) : (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height={defaultMode ? '80%' : '100%'}
      width="100%"
      position="relative"
    >
      <PageCallout
        headerIcon={topIcon}
        title={titleToUse || ''}
        description={descToUse || ''}
        link={!isClient ? learnMoreLinkUrl || '' : ''}
        linkText="Learn more"
        actionButtonRenderer={renderActionButton}
      />
    </Box>
  );
};
